import { useCallback, useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Coins, Loader2, Plus, Trash2, Pencil, Camera, Sparkles, ReceiptText, PiggyBank, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  ["rent_payment", "Rent payment"], ["deposit_paid", "Security deposit paid"],
  ["partial_payment", "Partial payment"], ["fee", "Fee"], ["credit", "Credit"],
  ["failed_payment", "Failed / returned payment"],
  ["partial_deposit_return", "Partial deposit return"], ["final_deposit_return", "Final deposit return"],
];
const TYPE_LABEL = Object.fromEntries(TYPES.map(([k, v]) => [k, v]));
const METHODS = [["", "—"], ["bank", "Bank transfer"], ["zelle", "Zelle"], ["venmo", "Venmo"], ["check", "Check"], ["cash", "Cash"], ["portal", "Portal"], ["other", "Other"]];
const METHOD_LABEL = Object.fromEntries(METHODS.map(([k, v]) => [k, v]));
const DATE_SOURCES = [["user_entered_date", "I entered this date"], ["receipt_date", "From the receipt"], ["statement_date", "From a statement"]];
const fmtMoney = (n) => (typeof n === "number" ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export function MoneyTrail({ propertyId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/properties/${propertyId}/money-trail`); setData(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't open the money trail."); onClose?.(); }
    finally { setLoading(false); }
  }, [propertyId, onClose]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await api.delete(`/properties/${propertyId}/money-trail/entries/${id}`); toast.success("Entry deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete."); }
  };

  const readOnly = data?.read_only;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent data-testid="money-trail" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Coins size={18} className="text-brand" /> Money trail</DialogTitle>
          <DialogDescription>Your factual record of money connected to this tenancy.</DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="flex items-center justify-center py-10 text-[#64748B]" data-testid="mt-loading"><Loader2 className="animate-spin" size={20} /></div>
        ) : (
          <div className="space-y-4 text-sm text-[#0F172A]">
            {!readOnly && (
              <Button size="sm" onClick={() => { setEditing(null); setAddOpen(true); }} data-testid="mt-add-btn" className="rounded-lg h-9 w-full bg-brand hover:bg-brand-hover text-white text-xs">
                <Plus size={14} className="mr-1.5" /> Add a money entry
              </Button>
            )}

            {/* Totals — TenantSeal-recorded only */}
            {Object.keys(data.totals).length > 0 && (
              <div data-testid="mt-totals">
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(data.totals).map(([k, v]) => (
                    <span key={k} data-testid={`mt-total-${k}`} className="inline-flex items-center gap-1 rounded-full border border-[#E2E8F0] bg-[#F8FAFC] px-2.5 py-1 text-[11px]">
                      <span className="font-semibold">{v.label}</span>
                      <span className="text-[#64748B]">{v.count} · {fmtMoney(v.sum)}</span>
                    </span>
                  ))}
                </div>
                <p className="mt-1 text-[11px] text-[#94A3B8]" data-testid="mt-totals-basis">{data.totals_basis} · excludes anything not entered here.</p>
              </div>
            )}

            {/* Entries */}
            <div data-testid="mt-entries">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8]">Recorded entries</p>
              {data.entries.length === 0 && <p className="text-xs text-[#94A3B8] mt-1">No entries yet.</p>}
              <div className="mt-1.5 space-y-1.5">
                {data.entries.map((e) => (
                  <div key={e.payment_id} data-testid={`mt-entry-${e.payment_id}`} className="rounded-lg border border-[#E2E8F0] p-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold flex items-center gap-1.5 flex-wrap">
                          <span className="rounded-md bg-[#EEF2FF] text-brand px-1.5 py-0.5 text-[10px]">{e.type_label}</span>
                          <span className="text-brand">{fmtMoney(e.amount)}</span>
                          {e.period_label && <span className="text-[#64748B] font-normal">· {e.period_label}</span>}
                        </p>
                        <p className="text-[11px] text-[#94A3B8]">{fmtDate(e.paid_on)}{e.method ? ` · ${METHOD_LABEL[e.method] || e.method}` : ""}</p>
                        {e.note && <p className="text-xs text-[#64748B] mt-0.5">{e.note}</p>}
                        {e.linked_document && <p className="text-[10px] text-sky-600 mt-0.5" data-testid={`mt-entry-doc-${e.payment_id}`}>Linked document: {e.linked_document.filename}</p>}
                        {e.linked_charge_id && <p className="text-[10px] text-violet-600 mt-0.5" data-testid={`mt-entry-charge-${e.payment_id}`}>Linked deduction charge (referenced)</p>}
                        {(e.history || []).length > 0 && <p className="text-[10px] text-[#94A3B8] mt-0.5" data-testid={`mt-entry-history-${e.payment_id}`}>Corrected — earlier value kept</p>}
                        {e.screenshots?.length > 0 && (
                          <div className="mt-1.5 flex gap-1.5">
                            {e.screenshots.map((p) => (
                              <a key={p.photo_id} href={fileUrl(p.storage_path)} target="_blank" rel="noopener noreferrer" className="h-12 w-12 rounded-lg overflow-hidden border border-[#E2E8F0]">
                                <img src={fileUrl(p.storage_path)} alt="" className="h-full w-full object-cover" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      {!readOnly && (
                        <div className="flex gap-0.5 shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => { setEditing(e); setAddOpen(true); }} data-testid={`mt-edit-${e.payment_id}`} className="h-7 w-7 p-0 rounded-lg text-[#64748B]"><Pencil size={13} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(e.payment_id)} data-testid={`mt-delete-${e.payment_id}`} className="h-7 w-7 p-0 rounded-lg text-[#FCA5A5]"><Trash2 size={13} /></Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Referenced deposit + returns */}
            <div data-testid="mt-deposit" className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] flex items-center gap-1.5"><PiggyBank size={12} /> Deposit &amp; returns (referenced)</p>
              <p className="text-xs mt-1">Deposit on record: <span className="font-semibold">{fmtMoney(data.referenced_deposit.deposit_amount)}</span></p>
              {data.referenced_deposit.return_status && (
                <p className="text-xs flex items-center gap-1"><RotateCcw size={11} className="text-[#64748B]" /> Return recorded: {data.referenced_deposit.return_status.replace("_", " ")}{data.referenced_deposit.amount_returned != null ? ` · ${fmtMoney(data.referenced_deposit.amount_returned)}` : ""}</p>
              )}
            </div>

            {/* Referenced deductions */}
            {data.referenced_deductions.length > 0 && (
              <div data-testid="mt-deductions">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] flex items-center gap-1.5"><ReceiptText size={12} /> Deduction charges (referenced from Deposit Auditor)</p>
                {data.referenced_deductions.map((d) => (
                  <p key={d.charge_id} data-testid={`mt-deduction-${d.charge_id}`} className="text-xs mt-1 text-[#334155]">{d.label} · {fmtMoney(d.amount)}</p>
                ))}
              </div>
            )}

            <p className="text-[11px] text-[#94A3B8] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2" data-testid="mt-disclaimer">{data.disclaimer}</p>
          </div>
        )}

        {addOpen && (
          <EntryDialog propertyId={propertyId} entry={editing} deductions={data?.referenced_deductions || []}
            onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function EntryDialog({ propertyId, entry, deductions, onClose, onSaved }) {
  const isEdit = !!entry;
  const [type, setType] = useState(entry?.entry_type || "deposit_paid");
  const [amount, setAmount] = useState(entry?.amount != null ? String(entry.amount) : "");
  const [paidOn, setPaidOn] = useState(entry?.paid_on?.slice(0, 10) || new Date().toISOString().slice(0, 10));
  const [dateSource, setDateSource] = useState(entry?.date_source || "user_entered_date");
  const [method, setMethod] = useState(entry?.method || "");
  const [note, setNote] = useState(entry?.note || "");
  const [period, setPeriod] = useState(entry?.period_label || "");
  const [charge, setCharge] = useState(entry?.linked_charge_id || "");
  const [photoIds, setPhotoIds] = useState([]);
  const [aiExtracted, setAiExtracted] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAnalyzing(true);
    try {
      const form = new FormData(); form.append("file", file);
      const { data } = await api.post(`/properties/${propertyId}/money-trail/analyze`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoIds((c) => [...c, data.photo_id]);
      const ext = data.ai_extracted || {}; setAiExtracted(ext);
      let did = false;
      if (ext.amount != null && !amount) { setAmount(String(ext.amount)); did = true; }
      if (ext.date) { setPaidOn(ext.date); setDateSource("receipt_date"); did = true; }
      setPrefilled(did);
      toast.success(did ? "Read from your receipt — confirm before saving." : "Receipt attached");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't read the receipt."); }
    finally { setAnalyzing(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter the amount."); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/properties/${propertyId}/money-trail/entries/${entry.payment_id}`, { amount: amt, paid_on: paidOn, method: method || null, note, period_label: period });
        toast.success("Entry corrected");
      } else {
        await api.post(`/properties/${propertyId}/money-trail/entries`, {
          entry_type: type, amount: amt, paid_on: paidOn, date_source: dateSource, method: method || null,
          note, period_label: period, linked_charge_id: charge || null, screenshot_photo_ids: photoIds, ai_extracted: aiExtracted,
        });
        toast.success("Entry recorded");
      }
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="mt-entry-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Correct entry" : "Add a money entry"}</DialogTitle>
          <DialogDescription>{isEdit ? "Once used in a sealed record, a correction keeps the earlier value." : "Amounts and dates are yours — TenantSeal does not verify them."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!isEdit && (
            <>
              <div>
                <Label className="text-xs">Type</Label>
                <select data-testid="mt-type-select" value={type} onChange={(e) => setType(e.target.value)} className="mt-1 h-10 w-full rounded-xl border border-input bg-background px-3 text-sm">
                  {TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="mt-file-input" onChange={onPickFile} />
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={analyzing} className="w-full rounded-xl" data-testid="mt-pick-btn">
                  {analyzing ? <><Loader2 size={16} className="animate-spin mr-2" /> Reading receipt…</> : <><Camera size={16} className="mr-2" /> {photoIds.length > 0 ? `${photoIds.length} attached — add another` : "Attach a receipt (optional)"}</>}
                </Button>
                {prefilled && <p className="mt-1.5 text-[11px] text-emerald-500 flex items-center gap-1" data-testid="mt-prefilled-note"><Sparkles size={12} /> Prefilled from your receipt — confirm below.</p>}
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount ($)</Label>
              <Input data-testid="mt-amount-input" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 h-10 rounded-xl" placeholder="0.00" />
            </div>
            <div>
              <Label className="text-xs">Date</Label>
              <Input data-testid="mt-date-input" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} className="mt-1 h-10 rounded-xl" />
            </div>
          </div>
          {!isEdit && (
            <div>
              <Label className="text-xs">Date source</Label>
              <select data-testid="mt-datesource-select" value={dateSource} onChange={(e) => setDateSource(e.target.value)} className="mt-1 h-10 w-full rounded-xl border border-input bg-background px-3 text-sm">
                {DATE_SOURCES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Method / source</Label>
              <select data-testid="mt-method-select" value={method} onChange={(e) => setMethod(e.target.value)} className="mt-1 h-10 w-full rounded-xl border border-input bg-background px-3 text-sm">
                {METHODS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Period (optional)</Label>
              <Input data-testid="mt-period-input" value={period} onChange={(e) => setPeriod(e.target.value)} className="mt-1 h-10 rounded-xl" placeholder="e.g. July 2026" />
            </div>
          </div>
          {!isEdit && deductions.length > 0 && (
            <div>
              <Label className="text-xs">Link a recorded deduction charge (optional)</Label>
              <select data-testid="mt-charge-select" value={charge} onChange={(e) => setCharge(e.target.value)} className="mt-1 h-10 w-full rounded-xl border border-input bg-background px-3 text-sm">
                <option value="">None</option>
                {deductions.map((d) => <option key={d.charge_id} value={d.charge_id}>{d.label} · {fmtMoney(d.amount)}</option>)}
              </select>
            </div>
          )}
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Input data-testid="mt-note-input" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 h-10 rounded-xl" placeholder="e.g. late fee waived" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="mt-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || analyzing} className="rounded-xl bg-brand hover:bg-brand-hover text-white" data-testid="mt-save">
            {saving ? <Loader2 size={16} className="animate-spin" /> : (isEdit ? "Save correction" : "Record entry")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
